import React from "react";
import { Box, Typography } from "@mui/material";
import { MAIN_COLOR_RED } from "../../assets/styles";

/**
 * ProgressStepper Component
 * 
 * A reusable progress stepper that displays multiple steps with active/inactive states.
 * 
 * @example
 * ```tsx
 * const [activeStep, setActiveStep] = useState(0);
 * const steps = ["Step 1", "Step 2", "Step 3"];
 * 
 * <ProgressStepper steps={steps} activeStep={activeStep} />
 * ```
 * 
 * @param steps - Array of step labels to display
 * @param activeStep - Index of the currently active step (0-based)
 */
interface ProgressStepperProps {
    steps: string[];
    activeStep: number;
}

const ProgressStepper: React.FC<ProgressStepperProps> = ({ steps, activeStep }) => {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 4,
                mt: 2,
                position: "relative",
                width: "100%",
            }}
        >
            {steps.map((stepLabel, index) => (
                <React.Fragment key={index}>
                    {/* Step Circle and Label */}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            position: "relative",
                            zIndex: 2,
                        }}
                    >
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                backgroundColor: activeStep >= index ? MAIN_COLOR_RED : "#e0e0e0",
                                color: "white",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: "bold",
                                mb: 1,
                            }}
                        >
                            {index + 1}
                        </Box>
                        <Typography
                            variant="caption"
                            sx={{
                                color: activeStep >= index ? MAIN_COLOR_RED : "#9e9e9e",
                                fontWeight: activeStep >= index ? "bold" : "normal",
                                fontSize: "0.75rem",
                            }}
                        >
                            {stepLabel}
                        </Typography>
                    </Box>

                    {/* Connector Line (only show if not the last step) */}
                    {index < steps.length - 1 && (
                        <Box
                            sx={{
                                width: 80,
                                height: 3,
                                backgroundColor: activeStep >= index + 1 ? MAIN_COLOR_RED : "#e0e0e0",
                                mx: 1,
                                position: "relative",
                                zIndex: 1,
                            }}
                        />
                    )}
                </React.Fragment>
            ))}
        </Box>
    );
};

export default ProgressStepper;

